import React, { useCallback, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { RotateCcwIcon } from 'lucide-react';
import { PresentationBar, type View } from './components/PresentationBar';
import { App as PoteligeoSnak } from './components/poteligeo-snak/App';
import { ConceptModal } from './components/concept/ConceptModal';
import { IntroStatement } from './components/IntroStatement';
import { InsightsView } from './components/insights/InsightsView';
import { PhoneViewport } from './components/PhoneViewport';
import { PhoneCue, PhoneCueInline } from './components/PhoneCue';
type AppProps = {
  /** Open the concept window on load. */
  conceptOpenOnLoad?: boolean;
};
export function App({
  conceptOpenOnLoad = false
}: AppProps) {
  const [view, setView] = useState<View>('main');
  const [conceptOpen, setConceptOpen] = useState(conceptOpenOnLoad);
  // Remounting the Snak restarts it at chapter 1 without touching its source.
  const [run, setRun] = useState(0);
  // Once the visitor touches the phone, the "try it" cue settles.
  const [engaged, setEngaged] = useState(false);
  const phoneRef = useRef<HTMLDivElement>(null);
  const openConcept = useCallback(() => setConceptOpen(true), []);
  const closeConcept = useCallback(() => setConceptOpen(false), []);
  const openInsights = useCallback(() => {
    setConceptOpen(false);
    setView('insights');
  }, []);
  const goHome = useCallback(() => setView('main'), []);
  return <div className="flex min-h-screen w-full flex-col bg-paper" data-id="element-0" data-mp="2znfy0adui2o6">
      <PresentationBar view={view} onOpenInsights={openInsights} onHome={goHome} data-id="element-1" data-mp="33n6xd2hftr4b" />
      <main className="relative flex flex-1 flex-col items-center md:justify-center md:py-8 lg:flex-row lg:items-center lg:gap-16 lg:px-10 xl:gap-24" data-id="element-2" data-mp="38l04bxqkep5n">
        <div className="order-2 w-full px-6 py-12 md:max-w-[480px] md:px-0 lg:order-1 lg:py-0" data-id="element-3" data-mp="nvj5hlha6osd">
          <IntroStatement onOpenConcept={openConcept} data-id="element-4" data-mp="sunby4ctm8f0" />
        </div>
        <div className="order-1 flex w-full flex-1 flex-col items-center md:w-auto md:flex-none lg:order-2" data-id="element-5" data-mp="sssq8ntq6c9v">
          <div ref={phoneRef} className="relative flex w-full flex-1 flex-col md:w-auto md:flex-none" onPointerDown={() => setEngaged(true)} data-id="element-6" data-mp="38kxqn1ac7l9">
            {/* Real handset proportions (~9:19.5): width follows the available height. */}
            <div className={`flex min-h-[640px] w-full flex-1 flex-col overflow-hidden bg-white transition-[box-shadow] duration-300 md:aspect-[9/19.5] md:h-[min(780px,calc(100vh-190px))] md:min-h-[620px] md:w-auto md:flex-none md:rounded-[44px] md:border-[10px] md:border-ink md:shadow-[0_40px_80px_-30px_rgba(14,27,51,0.45)] ${engaged ? '' : 'md:ring-[6px] md:ring-teal/15'}`} data-id="element-7" data-mp="85ully910b6z">
              <PhoneViewport data-id="element-8" data-mp="3ljpkevsylhyg">
                <PoteligeoSnak key={run} data-id="element-9" data-mp="3limrkic2t5fw" />
              </PhoneViewport>
            </div>
            <PhoneCue engaged={engaged} hostRef={phoneRef} data-id="element-10" data-mp="2xssfw43htmx8" />
          </div>
          <div className="flex items-center justify-center gap-3 px-6 py-3 md:mt-1 md:px-0 md:pb-0" data-id="element-11" data-mp="2sv0mf5n79sqx">
            <PhoneCueInline data-id="element-12" data-mp="18c6og57cpuff" />
            <p className="hidden font-mono text-[10px] tracking-[0.2em] text-graphite/70 xl:block" data-id="element-13" data-mp="18byb6c7bgn3p">POTELIGEO® SNAK · ~80 SEC</p>
            <span className="h-3 w-px bg-ink/15" aria-hidden="true" data-id="element-14" data-mp="yg3nv3s5e3ul" />
            <button type="button" onClick={() => setRun(r => r + 1)} className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-[12px] font-medium text-ink transition-[border-color,transform] duration-150 ease-out hover:border-ink active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy" data-id="element-15" data-mp="ydquzvkuq92r">
              <RotateCcwIcon size={12} strokeWidth={2.25} aria-hidden="true" data-id="element-16" data-mp="1m3uym2y1i8tr" />
              Restart
            </button>
          </div>
        </div>
        <AnimatePresence data-id="element-17" data-mp="aq0wyqzyz3xk">{view === 'insights' && <InsightsView onClose={goHome} data-id="element-18" data-mp="ar28ewkwdsft" />}</AnimatePresence>
      </main>
      <ConceptModal open={conceptOpen} onClose={closeConcept} data-id="element-19" data-mp="qwvrie5a2n6d" />
    </div>;
}